import React, { useState, useRef } from 'react';
import { useMessages } from '../hooks/useMessages';
import { useTemplates } from '../hooks/useTemplates';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { useProducts } from '../hooks/useProducts';
import { useToast } from '../context/ToastContext';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import type { Message } from '../types';

export const Messages: React.FC = () => {
  const { messages, loading, createMessage, deleteMessage, retryMessage } = useMessages();
  const { templates, processTemplate } = useTemplates();
  const { numbers } = useWhatsApp();
  const { products } = useProducts();
  const { showToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    templateId: '',
    whatsappNumberId: '',
    recipientName: '',
    productId: '',
  });
  const [preview, setPreview] = useState('');

  const updatePreview = () => {
    const template = templates.find(t => t.id === formData.templateId);
    const product = products.find(p => p.id === formData.productId);
    const number = numbers.find(n => n.id === formData.whatsappNumberId);

    if (!template) {
      setPreview('Selecione um template para ver o preview');
      return;
    }

    const variables = {
      nome: formData.recipientName || '{nome}',
      imovel: product ? product.title : '{imovel}',
      preco: product ? `R$ ${product.price.toLocaleString('pt-BR')}` : '{preco}',
      link: product ? `https://hollos.com/imovel/${product.id}` : '{link}',
      telefone: number ? number.phone : '{telefone}'
    };

    setPreview(processTemplate(template, variables));
  };

  React.useEffect(() => {
    updatePreview();
  }, [formData, templates, products, numbers]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.templateId || !formData.whatsappNumberId) {
      showToast('Selecione um template e um número WhatsApp', 'error');
      return;
    }

    try {
      const template = templates.find(t => t.id === formData.templateId);
      const number = numbers.find(n => n.id === formData.whatsappNumberId);
      const product = products.find(p => p.id === formData.productId);

      const variables = {
        nome: formData.recipientName || '{nome}',
        imovel: product ? product.title : '{imovel}',
        preco: product ? `R$ ${product.price.toLocaleString('pt-BR')}` : '{preco}',
        link: product ? `https://hollos.com/imovel/${product.id}` : '{link}',
        telefone: number ? number.phone : '{telefone}'
      };

      const processedContent = processTemplate(template!, variables);

      await createMessage({
        template_id: formData.templateId,
        template_name: template!.name,
        whatsapp_number_id: formData.whatsappNumberId,
        recipient_phone: number!.phone,
        recipient_name: formData.recipientName || number!.name,
        content: processedContent,
        product_id: formData.productId || undefined,
      });

      showToast('Mensagem adicionada à fila!', 'success');
      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      showToast(error.message || 'Erro ao criar mensagem', 'error');
    }
  };

  const handleRetry = async (id: string) => {
    try {
      await retryMessage(id);
      showToast('Mensagem colocada na fila para retry', 'success');
    } catch (error: any) {
      showToast(error.message || 'Erro ao tentar novamente', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta mensagem?')) {
      try {
        await deleteMessage(id);
        showToast('Mensagem excluída com sucesso!', 'success');
      } catch (error: any) {
        showToast(error.message || 'Erro ao excluir mensagem', 'error');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      templateId: '',
      whatsappNumberId: '',
      recipientName: '',
      productId: '',
    });
    setPreview('');
  };

  const getStatusLabel = (status: Message['status']) => {
    const labels = {
      'pending': 'Aguardando',
      'sending': 'Enviando',
      'sent': 'Enviado',
      'failed': 'Falhou'
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status: Message['status']) => {
    const classes = {
      'pending': 'badge-warning',
      'sending': 'badge-info',
      'sent': 'badge-success',
      'failed': 'badge-danger'
    };
    return classes[status] || 'badge-info';
  };

  if (loading) {
    return <div className="text-center py-8">Carregando mensagens...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium">Fila de Mensagens</h2>
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }}>
          + Nova Mensagem
        </Button>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-text-secondary">Nenhuma mensagem na fila</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <div key={message.id} className="message-item">
              <div className="flex justify-between items-start">
                <div className="message-info flex-1">
                  <h3 className="font-medium mb-2">
                    {message.template_name || 'Mensagem direta'}
                  </h3>
                  <div className="space-y-1 text-sm text-text-secondary">
                    <p><strong>Para:</strong> {message.recipient_name} ({message.recipient_phone})</p>
                    <p><strong>Conteúdo:</strong> {message.content.substring(0, 100)}{message.content.length > 100 ? '...' : ''}</p>
                    <p><strong>Criado em:</strong> {new Date(message.created_at).toLocaleString('pt-BR')}</p>
                    {message.sent_at && (
                      <p><strong>Enviado em:</strong> {new Date(message.sent_at).toLocaleString('pt-BR')}</p>
                    )}
                    {message.error && (
                      <p className="text-danger"><strong>Erro:</strong> {message.error}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`badge ${getStatusBadgeClass(message.status)}`}>
                    {getStatusLabel(message.status)}
                  </span>
                  {message.attempts > 0 && <small className="text-text-muted">Tentativas: {message.attempts}</small>}
                  <div className="flex gap-2">
                    {message.status === 'failed' && (
                      <Button variant="primary" size="sm" onClick={() => handleRetry(message.id)}>
                        🔄
                      </Button>
                    )}
                    <Button variant="danger" size="sm" onClick={() => handleDelete(message.id)}>
                      🗑️
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Enviar Mensagem"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => formRef.current?.requestSubmit()}>Enviar para Fila</Button>
          </>
        }
      >
        <form ref={formRef} onSubmit={handleSendMessage}>
          <div className="form-group">
            <label className="form-label">Template</label>
            <select
              className="form-select w-full"
              value={formData.templateId}
              onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
              required
            >
              <option value="">Selecione um template</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Número WhatsApp</label>
            <select
              className="form-select w-full"
              value={formData.whatsappNumberId}
              onChange={(e) => setFormData({ ...formData, whatsappNumberId: e.target.value })}
              required
            >
              <option value="">Selecione um número</option>
              {numbers.filter(n => n.status === 'active').map(number => (
                <option key={number.id} value={number.id}>{number.name} ({number.phone})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Nome do Destinatário</label>
            <input
              className="input w-full"
              value={formData.recipientName}
              onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Produto (opcional)</label>
            <select
              className="form-select w-full"
              value={formData.productId}
              onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
            >
              <option value="">Selecione um produto</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>{product.title}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Preview</label>
            <div className="input w-full bg-surface-1 min-h-24 p-3">
              {preview || 'Selecione um template para ver o preview'}
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};
